"use client";

import { cn } from "@/lib/utils";

const BLOB_SIZE =
  "h-[12rem] w-[12rem] sm:h-[15rem] sm:w-[15rem] md:h-[18rem] md:w-[18rem]";

/**
 * Halos cerca de los bordes de la card. La posición vive en el wrapper externo
 * y el scale/opacity en el interno para no pisar los translate.
 */
const CORNER_BLOBS = [
  {
    position: "left-[30%] top-[30%] -translate-x-1/2 -translate-y-1/2",
    pulse: "card-eclipse-pulse-a",
    rgb: "34,211,238",
    delay: "-0.7s"
  },
  {
    position: "right-[30%] top-[30%] translate-x-1/2 -translate-y-1/2",
    pulse: "card-eclipse-pulse-b",
    rgb: "251,146,60",
    delay: "-2.1s"
  },
  {
    position: "left-[30%] bottom-[30%] -translate-x-1/2 translate-y-1/2",
    pulse: "card-eclipse-pulse-c",
    rgb: "16,185,129",
    delay: "-1.4s"
  },
  {
    position: "right-[30%] bottom-[30%] translate-x-1/2 translate-y-1/2",
    pulse: "card-eclipse-pulse-d",
    rgb: "236,72,153",
    delay: "-3.2s"
  }
] as const;

export interface CardColorEclipseProps {
  entranceAnimation?: boolean;
}

/**
 * Eclipse de color pegado a la card: los blobs orbitan debajo de la superficie
 * blanca y se mantienen hacia el centro para que asomen por los bordes.
 */
export function CardColorEclipse({ entranceAnimation }: CardColorEclipseProps) {
  return (
    <div
      aria-hidden
      className={cn(
        "card-eclipse pointer-events-none absolute -inset-12 z-0 overflow-visible sm:-inset-16",
        entranceAnimation && "card-eclipse-enter"
      )}
    >
      <div className="card-eclipse-orbit absolute inset-0 origin-center">
        {CORNER_BLOBS.map((blob, index) => (
          <div key={index} className={cn("absolute", blob.position)}>
            <div
              className={cn(blob.pulse, "will-change-[transform,opacity]")}
              style={{ animationDelay: blob.delay }}
            >
              <div
                className={cn(BLOB_SIZE, "rounded-full blur-3xl")}
                style={{
                  background: `radial-gradient(circle at center, rgba(${blob.rgb},0.95) 0%, rgba(${blob.rgb},0.72) 30%, rgba(${blob.rgb},0.34) 52%, transparent 72%)`
                }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
